import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import SEO from '@/components/SEO';

type CommunityRequest = {
  id: string;
  requested_name: string;
  description: string;
  reason: string;
  category: string | null;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  user_id: string;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
};

export default function CommunityRequestsPage() {
  const [requests, setRequests] = useState<CommunityRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionDialog, setActionDialog] = useState<{ open: boolean; request: CommunityRequest | null; action: 'approve' | 'reject' }>({ open: false, request: null, action: 'approve' });
  const [adminNotes, setAdminNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const fetchRequests = async () => {
    setIsLoading(true);
    let query = supabase.from('community_requests').select('*').order('created_at', { ascending: false });
    if (statusFilter !== 'all') query = query.eq('status', statusFilter as 'pending' | 'approved' | 'rejected');
    const { data, error } = await query;
    if (error) { toast({ title: 'Error', description: error.message, variant: 'destructive' }); }
    else setRequests(data || []);
    setIsLoading(false);
  };

  const handleAction = async () => {
    if (!actionDialog.request) return;
    setIsSubmitting(true);
    const { id, requested_name, description, category, user_id } = actionDialog.request;
    const newStatus = actionDialog.action === 'approve' ? 'approved' : 'rejected';

    const { error: updateError } = await supabase.from('community_requests').update({
      status: newStatus,
      admin_notes: adminNotes || null,
      reviewed_at: new Date().toISOString(),
    }).eq('id', id);

    if (updateError) {
      toast({ title: 'Error', description: updateError.message, variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }

    if (actionDialog.action === 'approve') {
      const circleId = crypto.randomUUID();
      const { error: circleError } = await supabase.from('ThriveCircle').insert({
        id: circleId,
        name: requested_name,
        description: description,
        category: category,
        updatedAt: new Date().toISOString(),
        subscriptionPlan: 'THRIVE_STARTER',
      });

      if (circleError) {
        toast({ title: 'Circle creation failed', description: circleError.message, variant: 'destructive' });
      } else {
        const { error: memberError } = await supabase.from('ThriveCircleMember').insert({
          id: crypto.randomUUID(),
          circleId: circleId,
          userId: user_id,
          role: 'ADMIN',
          updatedAt: new Date().toISOString(),
        });
        if (memberError) toast({ title: 'Member add failed', description: memberError.message, variant: 'destructive' });
      }
    }

    toast({ title: `Request ${newStatus}`, description: `Community request has been ${newStatus}.` });
    setActionDialog({ open: false, request: null, action: 'approve' });
    setAdminNotes('');
    setIsSubmitting(false);
    fetchRequests();
  };

  const filtered = requests.filter(r =>
    r.requested_name.toLowerCase().includes(search.toLowerCase()) ||
    r.description.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status: string) => {
    const variants: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };
    return <Badge className={variants[status] || ''}>{status}</Badge>;
  };

  return (
    <>
      <SEO title="Community Requests" noindex />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Community Requests</h1>
          <p className="text-muted-foreground mt-1">Review and manage ThriveCircle community requests</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search requests..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No requests found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.requested_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{r.description}</TableCell>
                      <TableCell className="max-w-[150px] truncate">{r.reason}</TableCell>
                      <TableCell>{r.category || '—'}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{format(new Date(r.created_at), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="text-right">
                        {r.status === 'pending' && (
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => { setActionDialog({ open: true, request: r, action: 'approve' }); setAdminNotes(''); }}>
                              <Check className="h-4 w-4 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setActionDialog({ open: true, request: r, action: 'reject' }); setAdminNotes(''); }}>
                              <X className="h-4 w-4 mr-1" />Reject
                            </Button>
                          </div>
                        )}
                        {r.status !== 'pending' && r.admin_notes && (
                          <span className="text-xs text-muted-foreground italic">Note: {r.admin_notes}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={actionDialog.open} onOpenChange={open => !open && setActionDialog({ open: false, request: null, action: 'approve' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionDialog.action === 'approve' ? 'Approve' : 'Reject'} Community Request</DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'approve'
                ? `This will create a new ThriveCircle "${actionDialog.request?.requested_name}" and add the requester as admin.`
                : `This will reject the request for "${actionDialog.request?.requested_name}".`}
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Admin notes (optional)" value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, request: null, action: 'approve' })}>Cancel</Button>
            <Button onClick={handleAction} disabled={isSubmitting} variant={actionDialog.action === 'approve' ? 'default' : 'destructive'}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionDialog.action === 'approve' ? 'Approve & Create Circle' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
